/**
 * Visible scan field. A scanner can scan with focus anywhere (ScanContext
 * catches the burst), but this gives the operator an obvious place to scan
 * into and lets them TYPE a code by hand (damaged label, no scanner at hand):
 * Enter submits whatever is in the field.
 *
 * A real scan into this field is recognised by ScanContext, which removes the
 * characters again and passes the code to the active scan handler, and stops
 * the Enter — so onSubmit below only runs for typed codes.
 */

import { useState, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ScanLine } from 'lucide-react';
import { normalizeBarcode } from '../../lib/barcode';

interface ScanInputProps {
  onSubmit: (code: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  testId?: string;
}

const ScanInput = forwardRef<HTMLInputElement, ScanInputProps>(function ScanInput(
  { onSubmit, autoFocus, disabled, testId },
  ref
) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');

  return (
    <div className="relative">
      <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue pointer-events-none" />
      <input
        ref={ref}
        type="text"
        value={value}
        autoFocus={autoFocus}
        disabled={disabled}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          const code = normalizeBarcode(value);
          setValue('');
          if (code) onSubmit(code);
        }}
        placeholder={t('scan.scanFieldPlaceholder')}
        className="input w-full pl-10 font-mono"
        autoComplete="off"
        data-testid={testId}
      />
    </div>
  );
});

export default ScanInput;
